











import Vue from 'vue';
import { Component, InjectReactive, Prop } from 'vue-property-decorator';
import moment from 'moment-timezone';

import {
	Bullet,
	Circle,
	Color,
	color,
	Label,
	Legend,
	Modal,
	percent,
	Root,
	Scrollbar,
	Tooltip,
} from '@amcharts/amcharts5';
import {
	XYChart,
	LineSeries,
	DateAxis,
	ValueAxis,
	AxisRendererX,
	AxisRendererY,
	AxisRenderer,
	XYCursor,
} from '@amcharts/amcharts5/xy';

import {
	FeedApi,
	FeedTank,
	FeedTankGraphItem,
	Sensor,
} from '@/api';
import { ApiUtils } from '@/common/ApiUtils';

import DatePicker from '@/components/form pickers/DatePicker.vue';
import { DateRange } from '@/models/date-range';
import { FeedTankGraphData } from '@/pages/account/products/feed/util/feed-tank-util';
import { TooltipLabelBuilder } from '@/pages/account/products/feed/util/tooltip-label-builder';


@Component({
	components: {
		DatePicker,
	},
})
export default class FeedTankStrainChart extends Vue {
	@Prop({ required: true, type: Object })
	private feedTank!: FeedTank;

	@InjectReactive('feedProduct')
	private feedProduct!: any;

	public hasData(): boolean {
		return Object.values(this.graphData ?? {}).some(values => values.length > 0);
	}

	public errorMessage: string | null = null;
	public loading: boolean = true;
	/** Keys: 'weight' | 'reading/{sensorId}' */
	public graphData?: Record<string, FeedTankGraphItem[]>;


	private feedApi: FeedApi = new FeedApi();
	private sensorOutsideSamplingSeries: LineSeries[] = [];
	private sensorSamplingSeries: LineSeries[] = [];
	private weightSeries?: LineSeries;
	private yAxisReadings?: ValueAxis<AxisRenderer>;
	private yAxisWeight?: ValueAxis<AxisRenderer>;
	private yMin: number = 0;
	private yMax: number = 0;
	private readonly sensorOutsideSamplingColors = [color('#071852'), color('#0a2e0c')];
	private readonly sensorSamplingColors = [color('#1e88e5'), color('#43a047')];
	/** Must match the xAxis baseInterval below */
	private readonly baseIntervalMinutes = 15;
	/** Must match the series autoGapCount below */
	private readonly autoGapCount = 16;
	private noDataModal?: Modal;
	private xAxis?: DateAxis<AxisRenderer>;

	public async created(): Promise<void> {
		if (!this.feedTank.id) {
			this.errorMessage = 'Invalid feed tank provided';
			this.loading = false;
			return;
		}
		await this.fetchGraphData(this.feedTank.id);
		this.createChart();
	}

	public async onDateRangeChanged(newDateRange: Omit<DateRange, 'shortcut'>): Promise<void> {
		const startDate = moment(newDateRange.start, 'DD-MM-YYYY');
		const endDate = moment(newDateRange.end, 'DD-MM-YYYY').add(1, 'days');
		const offset = moment()
			.endOf('day')
			.diff(endDate, 'days');
		const nDays = endDate.diff(startDate, 'days') + 1;

		await this.fetchGraphData(this.feedTank.id!, offset, nDays);

		this.yAxisReadings?.setAll({ min: this.yMin, max: this.yMax });

		this.xAxis?.zoom(0, 1);
		this.sensorOutsideSamplingSeries.forEach((line, i) => {
			const key = this.getStrainReadingsKey(i);
			if (!key) return;
			const readings = this.graphData?.[key] ?? [];
			line.data.setAll(this.formatAllReadings(readings));
			this.sensorSamplingSeries[i]?.data.setAll(this.formatStrainReadings(readings, true));
		});

		const weightData = this.formatWeightData(this.graphData?.weight ?? []);
		this.weightSeries?.data.setAll(weightData);
		this.yAxisWeight?.setAll({ max: this.getWeightAxisMax(weightData) });

		if (!this.hasData()) {
			this.noDataModal?.open();
		} else {
			this.noDataModal?.close();
		}

		this.xAxis?.setAll({ min: startDate.valueOf(), max: endDate.valueOf() });
	}

	private async fetchGraphData(
		feedTankId: number,
		daysOffset: number | undefined = 0,
		nDays: number | undefined = 14,
	): Promise<void> {
		try {
			const graphDataResponse = await this.feedApi.feedTanksIdGraphDataGet(
				feedTankId,
				nDays,
				'weight,reading',
				daysOffset?.toString(),
			);

			// @ts-ignore (API returns 'reading/xyz', openapi can't dynamically generate the sensor keys)
			const data = graphDataResponse.data.data as Record<string, FeedTankGraphItem[]> | undefined;

			this.graphData = data;
			this.errorMessage = null;

			const allValues = (this.feedTank.sensors ?? []).flatMap((_, i) => {
				const key = this.getStrainReadingsKey(i);
				return key ? (data?.[key] ?? []).flatMap(r => r.value != null ? [r.value] : []) : [];
			});
			if (allValues.length) {
				const min = Math.min(...allValues);
				const max = Math.max(...allValues);
				// Fall back to a fixed padding when all readings are identical (e.g. a single
				// reading), otherwise the axis range collapses to a hairline decimal span.
				const padding = (max - min) * 0.2 || 1;
				this.yMin = min - padding;
				this.yMax = max + padding;
			}
		} catch (e) {
			const errorResponse = ApiUtils.handleApiError(e, 'Failed to load graph data');
			this.errorMessage = errorResponse.errorMessage;
		} finally {
			this.loading = false;
		}
	}

	private getStrainReadingsKey(index: number): string | null {
		const sensors = this.feedTank.sensors ?? [];

		if (sensors[index]?.id) return `reading/${sensors[index].id}`;

		return null;
	}

	private sensorDisplayName(sensor: Sensor, index: number): string {
		return sensor.serialNumber // Human readable name
			?? sensor.uniqueId // Machine readable name
			?? `Sensor ${sensor.id ?? index + 1}`;
	}

	private get siteTimezone(): string | null {
		return this.feedProduct?.site?.address?.timezone ?? null;
	}

	private isWithinSamplingWindow(timestamp: number): boolean {
		const hour = this.siteTimezone
			? moment(timestamp).tz(this.siteTimezone).hours()
			: new Date(timestamp).getHours();
		return hour >= 23 || hour < 4;
	}

	/**
	 * Flags points with no neighbor close enough for amCharts to draw a connecting
	 * line to (i.e. further apart than baseIntervalMinutes * autoGapCount), so they
	 * can be given a bullet - otherwise they'd render as nothing at all.
	 */
	private markIsolatedPoints<T extends { date: number }>(items: T[]): (T & { isolated: boolean })[] {
		const gapThresholdMs = this.baseIntervalMinutes * this.autoGapCount * 60 * 1000;
		return items.map((item, i) => {
			const prevGap = i > 0 ? item.date - items[i - 1].date : Infinity;
			const nextGap = i < items.length - 1 ? items[i + 1].date - item.date : Infinity;
			return { ...item, isolated: prevGap > gapThresholdMs && nextGap > gapThresholdMs };
		});
	}

	private formatAllReadings(items: FeedTankGraphItem[]): { value: number; date: number; isolated: boolean }[] {
		const formatted = items.map(item => ({ value: Math.round((item.value ?? 0) * 100) / 100, date: new Date(item.date!).getTime() }));
		return this.markIsolatedPoints(formatted);
	}

	private formatStrainReadings(
		items: FeedTankGraphItem[],
		isSamplingWindow: boolean,
	): { value: number; date: number; isolated: boolean }[] {
		const result: { value: number; date: number }[] = [];
		for (let i = 0; i < items.length; i++) {
			const date = new Date(items[i].date!).getTime();
			const isWithinSamplingWindow = this.isWithinSamplingWindow(date);
			const prevIsWithinSamplingWindow = i > 0
				? this.isWithinSamplingWindow(new Date(items[i - 1].date!).getTime())
				: isWithinSamplingWindow;
			const nextIsWithinSamplingWindow = i < items.length - 1
				? this.isWithinSamplingWindow(new Date(items[i + 1].date!).getTime())
				: isWithinSamplingWindow;
			// For the outside-sampling series, extend by one point at both boundaries
			// so the line visually meets the sampling series at 11 PM and 4 AM
			const isBoundaryPoint = !isSamplingWindow && (
				!prevIsWithinSamplingWindow || !nextIsWithinSamplingWindow
			);
			if (isWithinSamplingWindow === isSamplingWindow || isBoundaryPoint) {
				result.push({ value: Math.round((items[i].value ?? 0) * 100) / 100, date });
			}
		}
		return this.markIsolatedPoints(result);
	}

	private get nominalWeightTonnes(): number {
		return (this.feedTank.nominalWeight ?? 1000) / 1000;
	}

	/**
	 * The weight axis max is always at least the tank's nominal capacity, but
	 * extends further when readings go over capacity so they remain visible.
	 */
	private getWeightAxisMax(weightData: { value: number }[]): number {
		const actualMaxTonnes = weightData.length ? Math.max(...weightData.map(d => d.value)) : 0;
		return Math.max(this.nominalWeightTonnes, actualMaxTonnes) * 1.1;
	}

	private formatWeightData(items: FeedTankGraphItem[]): { value: number; date: number; text: string }[] {
		return items.map(item => {
			const primary = new FeedTankGraphData(this.feedTank, item, true);
			const tonnes = primary.value / 1000;
			const roundedTonnes = Math.round(tonnes * 10) / 10;
			const text = roundedTonnes.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + ' t';
			return {
				value: tonnes,
				date: new Date(item.date!).getTime(),
				text,
			};
		});
	}

	private createChart(): void {
		const root: Root = Root.new('feedTankStrainChart');
		const chart: XYChart = XYChart.new(root, {
			panX: false,
			panY: false,
			wheelX: 'zoomX',
		});

		const weightData = this.formatWeightData(this.graphData?.weight ?? []);

		const {
			cursor,
			xAxis,
			yAxisReadings,
			yAxisWeight,
			sensorOutsideSamplingSeries,
			sensorSamplingSeries,
			weightSeries,
			legend,
			noDataModal,
		} = this.createChartFeatures(root, this.yMin, this.yMax, this.getWeightAxisMax(weightData));

		this.xAxis = xAxis;
		this.yAxisReadings = yAxisReadings;
		this.yAxisWeight = yAxisWeight;
		this.sensorOutsideSamplingSeries = sensorOutsideSamplingSeries;
		this.sensorSamplingSeries = sensorSamplingSeries;
		this.weightSeries = weightSeries;
		this.noDataModal = noDataModal;

		chart.set('cursor', cursor);

		chart.xAxes.push(xAxis);
		chart.yAxes.push(yAxisReadings);
		chart.yAxes.push(yAxisWeight);
		sensorSamplingSeries.forEach((s, i) => {
			if (sensorOutsideSamplingSeries[i]) chart.series.push(sensorOutsideSamplingSeries[i]);
			chart.series.push(s);
		});
		chart.series.push(weightSeries);

		const scrollbarX = Scrollbar.new(root, { orientation: 'horizontal' });
		chart.set('scrollbarX', scrollbarX);

		chart.bottomAxesContainer.children.push(legend);

		const sensors = this.feedTank.sensors ?? [];
		sensors.forEach((_s, i) => {
			const key = this.getStrainReadingsKey(i);
			if (!key) return;

			const readings = this.graphData?.[key] ?? [];
			this.sensorOutsideSamplingSeries[i]?.data.setAll(this.formatAllReadings(readings));
			this.sensorSamplingSeries[i]?.data.setAll(this.formatStrainReadings(readings, true));
		});

		this.weightSeries?.data.setAll(weightData);

		const interleavedSeries = sensors.flatMap((_, i) => [
			sensorSamplingSeries[i],
			sensorOutsideSamplingSeries[i],
		]).filter(Boolean);
		legend.data.setAll([...interleavedSeries, weightSeries]);

		root.container.children.push(chart);

		sensorSamplingSeries.forEach(s => s.appear(1000));
		sensorOutsideSamplingSeries.forEach(s => s.appear(1000));
		weightSeries.appear(1000);
		chart.appear(1000, 100);

		sensorOutsideSamplingSeries.forEach((outsideSeries, i) => {
			const samplingSeries = sensorSamplingSeries[i];
			if (!samplingSeries) return;
			outsideSeries.on('visible', visible => {
				samplingSeries.get('tooltip')?.set('forceHidden', visible);
			});
		});
	}

	private createChartFeatures(root: Root, yMin: number, yMax: number, weightAxisMax: number): {
		cursor: XYCursor;
		xAxis: DateAxis<AxisRenderer>;
		yAxisReadings: ValueAxis<AxisRenderer>;
		yAxisWeight: ValueAxis<AxisRenderer>;
		sensorOutsideSamplingSeries: LineSeries[];
		sensorSamplingSeries: LineSeries[];
		weightSeries: LineSeries;
		legend: Legend;
		noDataModal: Modal;
	} {
		const xAxis: DateAxis<AxisRenderer> = DateAxis.new(root, {
			baseInterval: { timeUnit: 'minute', count: this.baseIntervalMinutes },
			renderer: AxisRendererX.new(root, {}),
			tooltip: Tooltip.new(root, {}),
			min: Date.now() - 86400 * 1000 * 14,
			max: Date.now(),
		});

		const yAxisReadings: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, {}),
			min: yMin,
			max: yMax,
			strictMinMax: true,
		});

		yAxisReadings.get("renderer")?.grid.template.set("visible", false);

		const yAxisWeight: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, { opposite: true }),
			min: 0,
			max: weightAxisMax,
			strictMinMax: true,
		});
		yAxisWeight.children.push(Label.new(root, {
			text: 'Tonnes',
			rotation: -90,
			y: percent(50),
			centerX: percent(50),
		}));
		yAxisWeight.get("renderer")?.grid.template.set("visible", false);

		const cursor: XYCursor = XYCursor.new(root, { behavior: 'zoomX' });
		cursor.lineX.set('visible', true);
		cursor.lineY.set('visible', true);

		const sensors = this.feedTank.sensors ?? [];
		const weightBulletColor = Color.fromRGB(213, 62, 79);

		const capacityRange = yAxisWeight.createAxisRange(yAxisWeight.makeDataItem({ value: this.nominalWeightTonnes }));
		capacityRange.get('grid')?.setAll({
			stroke: weightBulletColor,
			strokeDasharray: [4, 4],
			strokeOpacity: 0.8,
			strokeWidth: 1,
			visible: true,
		});
		capacityRange.get('label')?.setAll({
			text: 'Weight Capacity',
			fill: weightBulletColor,
			inside: true,
			centerX: percent(100),
			centerY: percent(100),
			paddingBottom: 2,
		});

		const makeTooltip = (fillColor: Color) => {
			const tooltip = Tooltip.new(root, {
				getFillFromSprite: false,
				labelText: new TooltipLabelBuilder()
					.line('{name}', { fontSize: 12, color: '#ffffff' })
					.line('{valueY}', { fontSize: 14, bold: true, color: '#ffffff' })
					.line('{valueX.formatDate("yyyy-MM-dd HH:mm:ss")}', {
						color: '#e8e8e8',
						fontSize: 10,
					})
					.build(),
			});
			tooltip.get('background')?.setAll({
				fill: fillColor,
				fillOpacity: 0.8,
				strokeOpacity: 0,
			});
			return tooltip;
		};

		const makeSensorSeries = (strokeColor: Color, name: string, connect: boolean) => {
			const series = LineSeries.new(root, {
				name,
				xAxis,
				yAxis: yAxisReadings,
				valueXField: 'date',
				valueYField: 'value',
				connect,
				snapTooltip: false,
				showTooltipOn: 'hover',
				autoGapCount: this.autoGapCount,
				stroke: strokeColor,
				tooltip: makeTooltip(strokeColor),
			});
			// Without a bullet, a reading with no neighbor close enough to connect a
			// line to (e.g. the first reading from a newly installed sensor) renders nothing.
			series.bullets.push((r, _s, dataItem) => {
				const context = dataItem.dataContext as { isolated?: boolean; date?: number } | undefined;
				// The legend builds its sample icon by calling this with an empty data
				// item (no `date`) - always give it a dot so the legend shows a spot.
				const isLegendPreview = !context || context.date === undefined;
				if (!isLegendPreview && !context?.isolated) return undefined;
				return Bullet.new(r, {
					sprite: Circle.new(r, {
						radius: 3,
						fill: strokeColor,
					}),
				});
			});
			return series;
		};

		const sensorOutsideSamplingSeries: LineSeries[] = sensors.map((s, i) => {
			const dayColor = this.sensorOutsideSamplingColors[i] ?? color('#666');
			return makeSensorSeries(dayColor, `${this.sensorDisplayName(s, i)} (All)`, false);
		});
		sensorOutsideSamplingSeries.forEach(s => s.strokes.template.setAll({ strokeDasharray: [4, 2],})); 

		const sensorSamplingSeries: LineSeries[] = sensors.map((s, i) => {
			const nightColor = this.sensorSamplingColors[i] ?? color('#333');
			return makeSensorSeries(nightColor, this.sensorDisplayName(s, i), false);
		});
		sensorSamplingSeries.forEach(s => {
			s.strokes.template.setAll({ strokeWidth: 3 });
			s.get('tooltip')?.set('forceHidden', true);
		});

		const weightTooltip = Tooltip.new(root, {
			getFillFromSprite: false,
			labelText: new TooltipLabelBuilder()
				.line('Tank Weight', { fontSize: 12 })
				.line('{text}', { fontSize: 14, bold: true })
				.line('{valueX.formatDate("yyyy-MM-dd HH:mm:ss")}', {
					color: '#e8e8e8',
					fontSize: 10,
				})
				.build(),
		});
		weightTooltip.get('background')?.setAll({
			fill: weightBulletColor,
			fillOpacity: 0.8,
			strokeOpacity: 0,
		});

		const weightSeries: LineSeries = LineSeries.new(root, {
			name: 'Tank Weight',
			xAxis,
			yAxis: yAxisWeight,
			valueXField: 'date',
			valueYField: 'value',
			connect: false,
			snapTooltip: true,
			showTooltipOn: 'hover',
			tooltip: weightTooltip,
		});

		weightSeries.bullets.push(r =>
			Bullet.new(r, {
				locationX: 0.5,
				locationY: 0.5,
				sprite: Circle.new(r, {
					radius: 5,
					fill: weightBulletColor,
					stroke: Color.fromRGB(255, 255, 255),
					strokeWidth: 2,
				}),
			}),
		);
		weightSeries.set('fill', weightBulletColor);
		
		const legend: Legend = Legend.new(root, {
			x: percent(50),
			centerX: percent(50),
		});
		const noDataModal: Modal = Modal.new(root, {
			content: 'No graph data for this feed tank, over this range',
		});
		return {
			cursor,
			xAxis,
			yAxisReadings,
			yAxisWeight,
			sensorOutsideSamplingSeries,
			sensorSamplingSeries,
			weightSeries,
			legend,
			noDataModal,
		};
	}
}
